import { useEffect } from 'react';
import { Nav } from './components/Nav';
import { Hero } from './components/Hero';
import { TrustStrip } from './components/TrustStrip';
import { PlatformIntro } from './components/PlatformIntro';
import { ReportSections } from './components/ReportSections';
import { ScheduleConsultation } from './components/ScheduleConsultation';
import { DemoVideo } from './components/DemoVideo';
import { AzureMarketplace } from './components/AzureMarketplace';
import { Footer } from './components/Footer';
import { LegalPage } from './components/LegalPage';
import { PackageQualifier } from './components/PackageQualifier';
import { BlogIndex } from './components/BlogIndex';
import { BlogArticle } from './components/BlogArticle';
import { LEGAL_DOCS } from './lib/legal';
import { findPost } from './lib/blog';

/**
 * Public marketing landing page for orbiswisdomx.com.
 *
 * Static content only — no router, no TanStack Query, no auth provider. The
 * single tie to the product app is the SSO href in ./lib/links.ts.
 *
 * Routing is a pathname match, not react-router: the landing bundle stays free
 * of a router dependency, and nginx's `try_files … /index.html` already hands
 * every path to this one document. Trailing slashes are tolerated because nginx
 * will serve /terms and /terms/ identically.
 *
 * /blog/ and each /blog/<slug>/ are the exception: they have their OWN built
 * HTML file (landing/blog/index.html, landing/blog/<slug>/index.html) so each
 * carries its own <head> for search engines and link previews. They still
 * mount this same App.
 *
 * Section order matches Figma `3jmrgfK0vysbMIQ8JgzKAV` node `2:38`, top to
 * bottom (1920×10476):
 *   Nav 2:533 · Hero 2:72 · TrustStrip 2:47 · PlatformIntro 2:87
 *   · ReportSections 2:90 (nine) · ScheduleConsultation 2:39 · DemoVideo 2:443
 *   · AzureMarketplace 2:459 · Footer 2:502
 */
function currentPath(): string {
  const raw = window.location.pathname.replace(/\/+$/, '');
  return raw === '' ? '/' : raw;
}

export default function App() {
  const path = currentPath();

  // Arriving from another page at /#section (e.g. the nav on a blog post): the
  // browser looks for the anchor before React has rendered it, so jump once
  // after mount. Same-page anchor clicks never reach this — no remount.
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);
  const legal = LEGAL_DOCS.find((d) => d.path === path);
  const isQualifier = path === '/qualifier';
  const isBlogIndex = path === '/blog';
  const blogSlug = path.startsWith('/blog/') ? path.slice('/blog/'.length) : null;
  const post = blogSlug ? findPost(blogSlug) : undefined;

  return (
    <>
      <Nav />
      {legal ? (
        <LegalPage doc={legal} />
      ) : isQualifier ? (
        <PackageQualifier />
      ) : isBlogIndex || (blogSlug && !post) ? (
        // An unknown slug falls back to the list rather than a dead page.
        <BlogIndex />
      ) : post ? (
        <BlogArticle post={post} />
      ) : (
        <main>
          <Hero />
          <TrustStrip />
          <PlatformIntro />
          <ReportSections />
          <ScheduleConsultation />
          <DemoVideo />
          <AzureMarketplace />
        </main>
      )}
      <Footer />
    </>
  );
}
